import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { 
  Plus, 
  AlertTriangle, 
  Package, 
  TrendingUp, 
  TrendingDown, 
  Edit, 
  Trash2,
  Search,
  X,
  Calendar,
  DollarSign,
  MapPin,
  BarChart3,
  Clock,
  CheckCircle,
  Loader2,
  XCircle,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import api from '../../services/api';
import { toast } from 'react-hot-toast';

interface ItemEstoque {
  id: number;
  nome: string;
  quantidade: number;
  quantidade_minima: number;
  unidade: string;
  preco_custo: number;
  fornecedor: string;
  categoria: string;
  ultima_atualizacao: string;
  status: 'normal' | 'baixo' | 'critico';
  // Novos campos
  produto_id?: number | null;
  data_vencimento?: string | null;
  data_fabricacao?: string | null;
  lote?: string | null;
  valor_vendido?: number | null;
  dias_alerta_vencimento?: number;
  localizacao?: string | null;
  empresa_id?: number;
  // Campos calculados
  status_vencimento?: 'sem_vencimento' | 'vencido' | 'proximo_vencimento' | 'valido';
  dias_para_vencer?: number | null;
  valor_total_estoque?: number;
  margem_lucro_percentual?: number | null;
}

interface MovimentoEstoque {
  id: number;
  item_id: number;
  tipo: 'entrada' | 'saida';
  quantidade: number;
  motivo: string;
  data: string;
  usuario: string;
  item_nome?: string;
  valor_unitario?: number | null;
  data_vencimento_lote?: string | null;
}

interface ItemForm {
  nome: string;
  quantidade: number;
  quantidade_minima: number;
  unidade: string;
  preco_custo: number;
  fornecedor: string;
  categoria: string;
  // Novos campos
  produto_id?: number | null;
  data_vencimento?: string | null;
  data_fabricacao?: string | null;
  lote?: string | null;
  valor_vendido?: number | null;
  dias_alerta_vencimento?: number;
  localizacao?: string | null;
  empresa_id?: number;
}

interface MovimentoForm {
  item_id: number;
  tipo: 'entrada' | 'saida';
  quantidade: number;
  motivo: string;
  valor_unitario?: number | null;
  data_vencimento_lote?: string | null;
}

interface EstatisticasEstoque {
  total_itens: number;
  total_quantidade: number;
  valor_total_estoque: number;
  itens_criticos: number;
  itens_baixos: number;
  itens_proximos_vencimento: number;
}

interface AlertasEstoque {
  criticos: number;
  baixos: number;
  vencidos: number;
  proximos_vencimento: number;
}

const EstoquePage: React.FC = () => {
  const [estoque, setEstoque] = useState<ItemEstoque[]>([]);
  const [movimentos, setMovimentos] = useState<MovimentoEstoque[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModalItem, setShowModalItem] = useState(false);
  const [showModalMovimento, setShowModalMovimento] = useState(false);
  const [editingItem, setEditingItem] = useState<ItemEstoque | null>(null);
  const [selectedItem, setSelectedItem] = useState<ItemEstoque | null>(null);
  const [abaAtiva, setAbaAtiva] = useState<'visao' | 'itens' | 'movimentos' | 'alertas'>('visao');
  const [estatisticas, setEstatisticas] = useState<EstatisticasEstoque | null>(null);
  const [alertas, setAlertas] = useState<AlertasEstoque | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [busca, setBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState<string>('todos');
  const [filtroCategoria, setFiltroCategoria] = useState<string>('todos');
  const [filtroVencimento, setFiltroVencimento] = useState<string>('todos');
  const [salvando, setSalvando] = useState(false);

  const {
    register: registerItem,
    handleSubmit: handleSubmitItem,
    reset: resetItem,
    watch: watchItem,
    formState: { errors: errorsItem }
  } = useForm<ItemForm>();

  const {
    register: registerMovimento,
    handleSubmit: handleSubmitMovimento,
    reset: resetMovimento,
    formState: { errors: errorsMovimento }
  } = useForm<MovimentoForm>();

  const dataVencimento = watchItem('data_vencimento');
  const dataFabricacao = watchItem('data_fabricacao');

  useEffect(() => {
    fetchEstoque();
    fetchMovimentos();
    fetchEstatisticas();
    fetchAlertas();
  }, []);

  const fetchEstoque = async () => {
    try {
      const response = await api.get('/api/estoque');
      setEstoque(response.data);
    } catch (error) {
      toast.error('Erro ao carregar estoque');
    } finally {
      setLoading(false);
    }
  };

  const fetchMovimentos = async () => {
    try {
      const response = await api.get('/api/estoque/movimentos');
      setMovimentos(response.data);
    } catch (error) {
      toast.error('Erro ao carregar movimentos');
    }
  };

  const fetchEstatisticas = async () => {
    try {
      const response = await api.get('/api/estoque/estatisticas');
      setEstatisticas(response.data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const fetchAlertas = async () => {
    try {
      const response = await api.get('/api/estoque/alertas');
      setAlertas(response.data);
    } catch (error) {
      console.error('Erro ao carregar alertas:', error);
    }
  };

  const onSubmitItem = async (data: ItemForm) => {
    setSalvando(true);
    try {
      // Validar data de vencimento não pode ser anterior à data de fabricação
      if (data.data_vencimento && data.data_fabricacao && new Date(data.data_vencimento) < new Date(data.data_fabricacao)) {
        toast.error('Data de vencimento não pode ser anterior à data de fabricação');
        setSalvando(false);
        return;
      }

      if (editingItem) {
        await api.put(`/api/estoque/${editingItem.id}`, data);
        toast.success('Item atualizado com sucesso!');
      } else {
        await api.post('/api/estoque', data);
        toast.success('Item adicionado com sucesso!');
      }
      setShowModalItem(false);
      setEditingItem(null);
      setCurrentStep(1);
      resetItem();
      fetchEstoque();
      fetchEstatisticas();
      fetchAlertas();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao salvar item');
    } finally {
      setSalvando(false);
    }
  };

  const onSubmitMovimento = async (data: MovimentoForm) => {
    try {
      await api.post('/api/estoque/movimentos', data);
      toast.success('Movimento registrado com sucesso!');
      setShowModalMovimento(false);
      resetMovimento();
      fetchEstoque();
      fetchMovimentos();
      fetchEstatisticas();
      fetchAlertas();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao registrar movimento');
    }
  };

  const handleEdit = (item: ItemEstoque) => {
    setEditingItem(item);
    resetItem({
      nome: item.nome,
      quantidade: item.quantidade,
      quantidade_minima: item.quantidade_minima,
      unidade: item.unidade,
      preco_custo: item.preco_custo,
      fornecedor: item.fornecedor,
      categoria: item.categoria,
      produto_id: item.produto_id || null,
      data_vencimento: item.data_vencimento || undefined,
      data_fabricacao: item.data_fabricacao || undefined,
      lote: item.lote || null,
      valor_vendido: item.valor_vendido || null,
      dias_alerta_vencimento: item.dias_alerta_vencimento || 15,
      localizacao: item.localizacao || null,
      empresa_id: item.empresa_id || 1
    });
    setCurrentStep(1);
    setShowModalItem(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este item?')) {
      return;
    }

    try {
      await api.delete(`/api/estoque/${id}`);
      toast.success('Item excluído com sucesso!');
      fetchEstoque();
      fetchEstatisticas();
      fetchAlertas();
    } catch (error) {
      toast.error('Erro ao excluir item');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'normal': return 'text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400';
      case 'baixo': return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'critico': return 'text-red-600 bg-red-100 dark:bg-red-900/20 dark:text-red-400';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-400';
    }
  };

  const getStatusVencimentoColor = (status?: string) => {
    switch (status) {
      case 'valido': return 'text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400';
      case 'proximo_vencimento': return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'vencido': return 'text-red-600 bg-red-100 dark:bg-red-900/20 dark:text-red-400';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-400';
    }
  };

  const getStatusVencimentoText = (status?: string) => {
    switch (status) {
      case 'valido': return 'Válido';
      case 'proximo_vencimento': return 'Próximo';
      case 'vencido': return 'Vencido';
      default: return 'Sem vencimento';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'normal': return <CheckCircle2 className="h-4 w-4" />;
      case 'baixo': return <AlertTriangle className="h-4 w-4" />;
      case 'critico': return <XCircle className="h-4 w-4" />;
      default: return null;
    }
  };

  const handleNextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Filtros
  const estoqueFiltrado = estoque.filter(item => {
    // Busca
    if (busca && !item.nome.toLowerCase().includes(busca.toLowerCase()) && 
        !item.categoria.toLowerCase().includes(busca.toLowerCase()) &&
        !(item.lote && item.lote.toLowerCase().includes(busca.toLowerCase())) &&
        !(item.localizacao && item.localizacao.toLowerCase().includes(busca.toLowerCase()))) {
      return false;
    }

    // Filtro de status
    if (filtroStatus !== 'todos' && item.status !== filtroStatus) {
      return false;
    }

    // Filtro de categoria
    if (filtroCategoria !== 'todos' && item.categoria !== filtroCategoria) {
      return false;
    }

    // Filtro de vencimento
    if (filtroVencimento !== 'todos') {
      if (filtroVencimento === 'com_vencimento' && !item.data_vencimento) return false;
      if (filtroVencimento === 'sem_vencimento' && item.data_vencimento) return false;
      if (filtroVencimento === 'proximo' && item.status_vencimento !== 'proximo_vencimento') return false;
      if (filtroVencimento === 'vencido' && item.status_vencimento !== 'vencido') return false;
    }

    return true;
  });

  const categorias = Array.from(new Set(estoque.map(item => item.categoria))).sort();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 md:h-80">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Carregando estoque...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 md:space-y-4 lg:space-y-6 px-2 md:px-4 lg:px-0">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 md:p-5 lg:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
          <div>
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2 md:gap-3">
              <Package className="h-6 w-6 md:h-7 md:w-7 lg:h-8 lg:w-8 text-blue-600" />
              Gestão de Estoque
            </h1>
            <p className="text-xs md:text-sm lg:text-base text-gray-600 dark:text-gray-400 mt-1">
              Controle completo de entrada, saída, vencimentos e alertas
            </p>
          </div>
          <div className="flex flex-wrap gap-2 md:gap-3">
            <button
              onClick={() => {
                setEditingItem(null);
                setCurrentStep(1);
                resetItem();
                setShowModalItem(true);
              }}
              className="flex items-center gap-2 px-3 md:px-4 py-2 md:py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-blue-700 transition-all text-sm md:text-base"
            >
              <Plus className="h-4 w-4 md:h-5 md:w-5" />
              <span className="hidden sm:inline">Novo Item</span>
              <span className="sm:hidden">Novo</span>
            </button>
            <button
              onClick={() => setShowModalMovimento(true)}
              className="flex items-center gap-2 px-3 md:px-4 py-2 md:py-2.5 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition-all text-sm md:text-base"
            >
              <TrendingUp className="h-4 w-4 md:h-5 md:w-5" />
              <span className="hidden sm:inline">Movimento</span>
              <span className="sm:hidden">Mov.</span>
            </button>
          </div>
        </div>
      </div>

      {/* Estatísticas Rápidas */}
      {estatisticas && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 md:p-4">
            <div className="flex items-center gap-2 mb-1">
              <Package className="h-4 w-4 md:h-5 md:w-5 text-blue-500" />
              <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400">Total Itens</p>
            </div>
            <p className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">
              {estatisticas.total_itens}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 md:p-4">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="h-4 w-4 md:h-5 md:w-5 text-green-500" />
              <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400">Valor Total</p>
            </div>
            <p className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">
              R$ {Number(estatisticas.valor_total_estoque || 0).toFixed(2)}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 md:p-4 border-l-4 border-red-500">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="h-4 w-4 md:h-5 md:w-5 text-red-500" />
              <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400">Críticos</p>
            </div>
            <p className="text-lg md:text-xl lg:text-2xl font-bold text-red-600">
              {estatisticas.itens_criticos}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 md:p-4 border-l-4 border-yellow-500">
            <div className="flex items-center gap-2 mb-1">
              <AlertCircle className="h-4 w-4 md:h-5 md:w-5 text-yellow-500" />
              <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400">Baixos</p>
            </div>
            <p className="text-lg md:text-xl lg:text-2xl font-bold text-yellow-600">
              {estatisticas.itens_baixos}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 md:p-4 border-l-4 border-orange-500">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="h-4 w-4 md:h-5 md:w-5 text-orange-500" />
              <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400">Próx. Venc.</p>
            </div>
            <p className="text-lg md:text-xl lg:text-2xl font-bold text-orange-600">
              {estatisticas.itens_proximos_vencimento}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 md:p-4">
            <div className="flex items-center gap-2 mb-1">
              <BarChart3 className="h-4 w-4 md:h-5 md:w-5 text-purple-500" />
              <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400">Total Qtd.</p>
            </div>
            <p className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">
              {estatisticas.total_quantidade}
            </p>
          </div>
        </div>
      )}

      {/* Alertas Consolidados */}
      {alertas && (alertas.criticos > 0 || alertas.baixos > 0 || alertas.vencidos > 0 || alertas.proximos_vencimento > 0) && (
        <div className="bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 border-2 border-red-200 dark:border-red-800 rounded-lg p-4 md:p-5">
          <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
            <AlertTriangle className="h-5 w-5 md:h-6 md:w-6 text-red-600 animate-pulse" />
            <h3 className="font-bold text-base md:text-lg text-red-900 dark:text-red-100">
              Alertas de Estoque
            </h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {alertas.criticos > 0 && (
              <div className="bg-red-100 dark:bg-red-900/30 rounded-lg p-3 border border-red-300 dark:border-red-700">
                <p className="text-xs md:text-sm text-red-700 dark:text-red-300 font-medium mb-1">Críticos</p>
                <p className="text-xl md:text-2xl font-bold text-red-600">{alertas.criticos}</p>
              </div>
            )}
            {alertas.baixos > 0 && (
              <div className="bg-yellow-100 dark:bg-yellow-900/30 rounded-lg p-3 border border-yellow-300 dark:border-yellow-700">
                <p className="text-xs md:text-sm text-yellow-700 dark:text-yellow-300 font-medium mb-1">Baixos</p>
                <p className="text-xl md:text-2xl font-bold text-yellow-600">{alertas.baixos}</p>
              </div>
            )}
            {alertas.vencidos > 0 && (
              <div className="bg-red-200 dark:bg-red-900/40 rounded-lg p-3 border-2 border-red-400 dark:border-red-600">
                <p className="text-xs md:text-sm text-red-800 dark:text-red-200 font-medium mb-1">Vencidos</p>
                <p className="text-xl md:text-2xl font-bold text-red-700">{alertas.vencidos}</p>
              </div>
            )}
            {alertas.proximos_vencimento > 0 && (
              <div className="bg-orange-100 dark:bg-orange-900/30 rounded-lg p-3 border border-orange-300 dark:border-orange-700">
                <p className="text-xs md:text-sm text-orange-700 dark:text-orange-300 font-medium mb-1">Próx. Venc.</p>
                <p className="text-xl md:text-2xl font-bold text-orange-600">{alertas.proximos_vencimento}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Menu de Abas */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex flex-wrap -mb-px overflow-x-auto" aria-label="Tabs">
            <button
              onClick={() => setAbaAtiva('visao')}
              className={`flex items-center gap-2 px-3 py-2.5 md:px-4 md:py-3 text-xs md:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                abaAtiva === 'visao'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <BarChart3 className="h-4 w-4" />
              <span className="hidden sm:inline">Visão Geral</span>
              <span className="sm:hidden">Visão</span>
            </button>
            <button
              onClick={() => setAbaAtiva('itens')}
              className={`flex items-center gap-2 px-3 py-2.5 md:px-4 md:py-3 text-xs md:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                abaAtiva === 'itens'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <Package className="h-4 w-4" />
              <span className="hidden sm:inline">Itens</span>
              <span className="sm:hidden">Itens</span>
            </button>
            <button
              onClick={() => setAbaAtiva('movimentos')}
              className={`flex items-center gap-2 px-3 py-2.5 md:px-4 md:py-3 text-xs md:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                abaAtiva === 'movimentos'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <TrendingUp className="h-4 w-4" />
              <span className="hidden sm:inline">Movimentos</span>
              <span className="sm:hidden">Mov.</span>
            </button>
            <button
              onClick={() => setAbaAtiva('alertas')}
              className={`flex items-center gap-2 px-3 py-2.5 md:px-4 md:py-3 text-xs md:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                abaAtiva === 'alertas'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <AlertTriangle className="h-4 w-4" />
              <span className="hidden sm:inline">Alertas</span>
              <span className="sm:hidden">Alertas</span>
            </button>
          </nav>
        </div>
      </div>

      {/* Conteúdo das Abas */}
      {abaAtiva === 'visao' && (
        <div className="space-y-4 md:space-y-6">
          {/* Filtros e Busca */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 md:p-5">
            <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
              <div className="flex-1 w-full md:w-auto">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 md:h-5 md:w-5" />
                  <input
                    type="text"
                    placeholder="Buscar por nome, categoria, lote ou localização..."
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-2 md:gap-3">
                <select
                  value={filtroStatus}
                  onChange={(e) => setFiltroStatus(e.target.value)}
                  className="px-3 md:px-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="todos">Todos os Status</option>
                  <option value="normal">Normal</option>
                  <option value="baixo">Baixo</option>
                  <option value="critico">Crítico</option>
                </select>
                <select
                  value={filtroCategoria}
                  onChange={(e) => setFiltroCategoria(e.target.value)}
                  className="px-3 md:px-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="todos">Todas as Categorias</option>
                  {categorias.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <select
                  value={filtroVencimento}
                  onChange={(e) => setFiltroVencimento(e.target.value)}
                  className="px-3 md:px-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="todos">Todos</option>
                  <option value="com_vencimento">Com Vencimento</option>
                  <option value="sem_vencimento">Sem Vencimento</option>
                  <option value="proximo">Próximo ao Vencimento</option>
                  <option value="vencido">Vencidos</option>
                </select>
              </div>
            </div>
          </div>

          {/* Grid de Itens */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
            {estoqueFiltrado.map((item) => (
              <div
                key={item.id}
                className={`bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden border-l-4 ${
                  item.status === 'critico' ? 'border-red-500' :
                  item.status === 'baixo' ? 'border-yellow-500' :
                  'border-green-500'
                }`}
              >
                <div className="p-4 md:p-5">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white truncate">
                        {item.nome}
                      </h3>
                      <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 truncate">
                        {item.categoria}
                      </p>
                    </div>
                    <div className="flex flex-col gap-1 ml-2">
                      <div className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(item.status)}`}>
                        {getStatusIcon(item.status)}
                        <span className="hidden sm:inline">{item.status === 'normal' ? 'Normal' : item.status === 'baixo' ? 'Baixo' : 'Crítico'}</span>
                      </div>
                      {item.status_vencimento && item.status_vencimento !== 'sem_vencimento' && (
                        <div className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusVencimentoColor(item.status_vencimento)}`}>
                          {getStatusVencimentoText(item.status_vencimento)}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Barra de Progresso */}
                  <div className="mb-3">
                    <div className="flex justify-between text-xs md:text-sm mb-1">
                      <span className="text-gray-600 dark:text-gray-400">Estoque</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {item.quantidade} / {item.quantidade_minima} {item.unidade}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${
                          item.status === 'critico' ? 'bg-red-500' :
                          item.status === 'baixo' ? 'bg-yellow-500' :
                          'bg-green-500'
                        }`}
                        style={{ width: `${Math.min(100, (item.quantidade / item.quantidade_minima) * 100)}%` }}
                      />
                    </div>
                  </div>

                  <div className="space-y-2 mb-4 text-xs md:text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Valor Custo:</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        R$ {Number(item.preco_custo || 0).toFixed(2)}
                      </span>
                    </div>
                    {item.valor_vendido && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Valor Venda:</span>
                        <span className="font-medium text-green-600">
                          R$ {Number(item.valor_vendido).toFixed(2)}
                        </span>
                      </div>
                    )}
                    {item.margem_lucro_percentual !== null && item.margem_lucro_percentual !== undefined && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Margem:</span>
                        <span className={`font-medium ${item.margem_lucro_percentual >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {item.margem_lucro_percentual.toFixed(1)}%
                        </span>
                      </div>
                    )}
                    {item.data_vencimento && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Vencimento:</span>
                        <span className={`font-medium ${
                          item.status_vencimento === 'vencido' ? 'text-red-600' :
                          item.status_vencimento === 'proximo_vencimento' ? 'text-yellow-600' :
                          'text-green-600'
                        }`}>
                          {new Date(item.data_vencimento).toLocaleDateString('pt-BR')}
                          {item.dias_para_vencer !== null && item.dias_para_vencer !== undefined && (
                            <span className="ml-1">({item.dias_para_vencer}d)</span>
                          )}
                        </span>
                      </div>
                    )}
                    {item.lote && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Lote:</span>
                        <span className="font-medium text-gray-900 dark:text-white">{item.lote}</span>
                      </div>
                    )}
                    {item.localizacao && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Local:</span>
                        <span className="font-medium text-gray-900 dark:text-white">{item.localizacao}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Fornecedor:</span>
                      <span className="font-medium text-gray-900 dark:text-white truncate ml-2">{item.fornecedor}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(item)}
                        className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                        title="Editar"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        title="Excluir"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedItem(item);
                        resetMovimento({ item_id: item.id, tipo: 'entrada', quantidade: 0, motivo: '' });
                        setShowModalMovimento(true);
                      }}
                      className="text-xs md:text-sm text-blue-600 hover:text-blue-700 font-medium px-2 py-1 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                    >
                      Movimento
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {estoqueFiltrado.length === 0 && (
            <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow">
              <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">Nenhum item encontrado com os filtros aplicados</p>
            </div>
          )}
        </div>
      )}

      {abaAtiva === 'itens' && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 md:p-5">
          <p className="text-gray-600 dark:text-gray-400">Visualize todos os itens na aba "Visão Geral"</p>
        </div>
      )}

      {abaAtiva === 'movimentos' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Movimentos Recentes
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Item
                    </th>
                    <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Quantidade
                    </th>
                    <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden md:table-cell">
                      Valor Unit.
                    </th>
                    <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden lg:table-cell">
                      Motivo
                    </th>
                    <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden md:table-cell">
                      Usuário
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {movimentos.slice(0, 20).map((movimento) => (
                    <tr key={movimento.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <td className="px-3 md:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {movimento.item_nome || estoque.find(item => item.id === movimento.item_id)?.nome || 'N/A'}
                      </td>
                      <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          movimento.tipo === 'entrada' 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        }`}>
                          {movimento.tipo === 'entrada' ? (
                            <TrendingUp className="h-3 w-3 mr-1" />
                          ) : (
                            <TrendingDown className="h-3 w-3 mr-1" />
                          )}
                          {movimento.tipo === 'entrada' ? 'Entrada' : 'Saída'}
                        </span>
                      </td>
                      <td className="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {movimento.quantidade}
                      </td>
                      <td className="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 hidden md:table-cell">
                        {movimento.valor_unitario ? `R$ ${Number(movimento.valor_unitario).toFixed(2)}` : '-'}
                      </td>
                      <td className="px-3 md:px-6 py-4 text-sm text-gray-500 dark:text-gray-400 hidden lg:table-cell">
                        {movimento.motivo}
                      </td>
                      <td className="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {new Date(movimento.data).toLocaleDateString('pt-BR')}
                      </td>
                      <td className="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 hidden md:table-cell">
                        {movimento.usuario}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {abaAtiva === 'alertas' && (
        <div className="space-y-4 md:space-y-6">
          {/* Itens Críticos */}
          {estoque.filter(item => item.status === 'critico').length > 0 && (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 md:p-5 border-l-4 border-red-500">
              <h3 className="text-base md:text-lg font-semibold text-red-600 mb-4 flex items-center gap-2">
                <XCircle className="h-5 w-5" />
                Itens Críticos ({estoque.filter(item => item.status === 'critico').length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                {estoque.filter(item => item.status === 'critico').map((item) => (
                  <div key={item.id} className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 border border-red-200 dark:border-red-800">
                    <p className="font-medium text-red-900 dark:text-red-100">{item.nome}</p>
                    <p className="text-sm text-red-700 dark:text-red-300">
                      {item.quantidade} {item.unidade} restantes (mínimo: {item.quantidade_minima})
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Itens Próximos ao Vencimento */}
          {estoque.filter(item => item.status_vencimento === 'proximo_vencimento' || item.status_vencimento === 'vencido').length > 0 && (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 md:p-5 border-l-4 border-orange-500">
              <h3 className="text-base md:text-lg font-semibold text-orange-600 mb-4 flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Vencimentos ({estoque.filter(item => item.status_vencimento === 'proximo_vencimento' || item.status_vencimento === 'vencido').length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                {estoque
                  .filter(item => item.status_vencimento === 'proximo_vencimento' || item.status_vencimento === 'vencido')
                  .sort((a, b) => {
                    if (!a.data_vencimento || !b.data_vencimento) return 0;
                    return new Date(a.data_vencimento).getTime() - new Date(b.data_vencimento).getTime();
                  })
                  .map((item) => (
                    <div key={item.id} className={`rounded-lg p-3 border ${
                      item.status_vencimento === 'vencido' 
                        ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                        : 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800'
                    }`}>
                      <p className="font-medium text-gray-900 dark:text-white">{item.nome}</p>
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        Vence: {item.data_vencimento ? new Date(item.data_vencimento).toLocaleDateString('pt-BR') : 'N/A'}
                        {item.dias_para_vencer !== null && item.dias_para_vencer !== undefined && (
                          <span className={`ml-2 font-bold ${
                            item.status_vencimento === 'vencido' ? 'text-red-600' : 'text-orange-600'
                          }`}>
                            ({item.dias_para_vencer < 0 ? 'Vencido há ' + Math.abs(item.dias_para_vencer) + ' dias' : item.dias_para_vencer + ' dias'})
                          </span>
                        )}
                      </p>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal Item - Multi-step */}
      {showModalItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto my-4">
            <div className="p-4 md:p-6">
              <div className="flex justify-between items-center mb-4 md:mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
                  {editingItem ? 'Editar Item' : 'Novo Item'}
                </h2>
                <button
                  onClick={() => {
                    setShowModalItem(false);
                    setEditingItem(null);
                    setCurrentStep(1);
                    resetItem();
                  }}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              {/* Indicador de Passos */}
              <div className="mb-6">
                <div className="flex items-center justify-between">
                  {[1, 2, 3].map((step) => (
                    <React.Fragment key={step}>
                      <div className="flex items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-medium ${
                          currentStep >= step
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                        }`}>
                          {currentStep > step ? <CheckCircle className="h-5 w-5" /> : step}
                        </div>
                        <span className={`ml-2 text-xs md:text-sm font-medium hidden sm:inline ${
                          currentStep >= step ? 'text-blue-600' : 'text-gray-500'
                        }`}>
                          {step === 1 ? 'Básicas' : step === 2 ? 'Vencimento' : 'Financeiro'}
                        </span>
                      </div>
                      {step < 3 && (
                        <div className={`flex-1 h-1 mx-2 ${
                          currentStep > step ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                        }`} />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
              
              <form onSubmit={handleSubmitItem(onSubmitItem)} className="space-y-4 md:space-y-6">
                {/* Passo 1: Informações Básicas */}
                {currentStep === 1 && (
                  <div className="space-y-4 md:space-y-6">
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                        Nome do Item <span className="text-red-500">*</span>
                      </label>
                      <input
                        {...registerItem('nome', { required: 'Nome é obrigatório' })}
                        type="text"
                        className="w-full px-3 md:px-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                        placeholder="Nome do item"
                      />
                      {errorsItem.nome && (
                        <p className="mt-1 text-xs md:text-sm text-red-600">{errorsItem.nome.message}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                      <div>
                        <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                          Quantidade <span className="text-red-500">*</span>
                        </label>
                        <input
                          {...registerItem('quantidade', { 
                            required: 'Quantidade é obrigatória',
                            min: { value: 0, message: 'Quantidade deve ser maior ou igual a 0' },
                            valueAsNumber: true
                          })}
                          type="number"
                          className="w-full px-3 md:px-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                          placeholder="0"
                        />
                        {errorsItem.quantidade && (
                          <p className="mt-1 text-xs md:text-sm text-red-600">{errorsItem.quantidade.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                          Quantidade Mínima <span className="text-red-500">*</span>
                        </label>
                        <input
                          {...registerItem('quantidade_minima', { 
                            required: 'Quantidade mínima é obrigatória',
                            min: { value: 0, message: 'Quantidade mínima deve ser maior ou igual a 0' },
                            valueAsNumber: true
                          })}
                          type="number"
                          className="w-full px-3 md:px-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                          placeholder="0"
                        />
                        {errorsItem.quantidade_minima && (
                          <p className="mt-1 text-xs md:text-sm text-red-600">{errorsItem.quantidade_minima.message}</p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                      <div>
                        <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                          Unidade <span className="text-red-500">*</span>
                        </label>
                        <input
                          {...registerItem('unidade', { required: 'Unidade é obrigatória' })}
                          type="text"
                          className="w-full px-3 md:px-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                          placeholder="kg, un, l, etc"
                        />
                        {errorsItem.unidade && (
                          <p className="mt-1 text-xs md:text-sm text-red-600">{errorsItem.unidade.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                          Preço Custo (R$) <span className="text-red-500">*</span>
                        </label>
                        <input
                          {...registerItem('preco_custo', { 
                            required: 'Preço custo é obrigatório',
                            min: { value: 0, message: 'Preço deve ser maior ou igual a 0' },
                            valueAsNumber: true
                          })}
                          type="number"
                          step="0.01"
                          className="w-full px-3 md:px-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                          placeholder="0.00"
                        />
                        {errorsItem.preco_custo && (
                          <p className="mt-1 text-xs md:text-sm text-red-600">{errorsItem.preco_custo.message}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                        Fornecedor <span className="text-red-500">*</span>
                      </label>
                      <input
                        {...registerItem('fornecedor', { required: 'Fornecedor é obrigatório' })}
                        type="text"
                        className="w-full px-3 md:px-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                        placeholder="Nome do fornecedor"
                      />
                      {errorsItem.fornecedor && (
                        <p className="mt-1 text-xs md:text-sm text-red-600">{errorsItem.fornecedor.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                        Categoria <span className="text-red-500">*</span>
                      </label>
                      <input
                        {...registerItem('categoria', { required: 'Categoria é obrigatória' })}
                        type="text"
                        className="w-full px-3 md:px-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                        placeholder="Categoria do item"
                      />
                      {errorsItem.categoria && (
                        <p className="mt-1 text-xs md:text-sm text-red-600">{errorsItem.categoria.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                        Localização
                      </label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 md:h-5 md:w-5" />
                        <input
                          {...registerItem('localizacao')}
                          type="text"
                          className="w-full pl-10 pr-3 md:pr-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                          placeholder="Ex: Prateleira A, Geladeira 1, etc"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Passo 2: Vencimento */}
                {currentStep === 2 && (
                  <div className="space-y-4 md:space-y-6">
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 md:p-4 mb-4">
                      <p className="text-xs md:text-sm text-blue-800 dark:text-blue-200">
                        <strong>Dica:</strong> Os campos de vencimento são opcionais. Preencha apenas se o item tiver data de validade.
                      </p>
                    </div>

                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                        Data de Fabricação
                      </label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 md:h-5 md:w-5" />
                        <input
                          {...registerItem('data_fabricacao')}
                          type="date"
                          className="w-full pl-10 pr-3 md:pr-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                        />
                      </div>
                      {dataVencimento && dataFabricacao && new Date(dataVencimento) < new Date(dataFabricacao) && (
                        <p className="mt-1 text-xs md:text-sm text-red-600">
                          Data de vencimento não pode ser anterior à data de fabricação
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                        Data de Vencimento
                      </label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 md:h-5 md:w-5" />
                        <input
                          {...registerItem('data_vencimento')}
                          type="date"
                          min={dataFabricacao || undefined}
                          className="w-full pl-10 pr-3 md:pr-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                        Número do Lote
                      </label>
                      <input
                        {...registerItem('lote')}
                        type="text"
                        className="w-full px-3 md:px-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                        placeholder="Ex: LOTE-2024-001"
                      />
                    </div>

                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                        Dias de Alerta antes do Vencimento
                      </label>
                      <input
                        {...registerItem('dias_alerta_vencimento', { 
                          valueAsNumber: true,
                          min: { value: 1, message: 'Mínimo 1 dia' }
                        })}
                        type="number"
                        defaultValue={15}
                        className="w-full px-3 md:px-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                        placeholder="15"
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        O sistema alertará quando faltarem X dias para o vencimento
                      </p>
                    </div>
                  </div>
                )}

                {/* Passo 3: Financeiro */}
                {currentStep === 3 && (
                  <div className="space-y-4 md:space-y-6">
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 md:p-4 mb-4">
                      <p className="text-xs md:text-sm text-green-800 dark:text-green-200">
                        <strong>Informação:</strong> O valor vendido é usado para calcular a margem de lucro. Se o item estiver vinculado a um produto, este valor será usado.
                      </p>
                    </div>

                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                        Valor Vendido (R$)
                      </label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 md:h-5 md:w-5" />
                        <input
                          {...registerItem('valor_vendido', { 
                            min: { value: 0, message: 'Valor deve ser maior ou igual a 0' },
                            valueAsNumber: true
                          })}
                          type="number"
                          step="0.01"
                          className="w-full pl-10 pr-3 md:pr-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                          placeholder="0.00"
                        />
                      </div>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Preço de venda do produto (opcional)
                      </p>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 md:p-4">
                      <p className="text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Resumo Financeiro:
                      </p>
                      <div className="space-y-1 text-xs md:text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Valor Pago (Custo):</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            R$ {Number(watchItem('preco_custo') || 0).toFixed(2)}
                          </span>
                        </div>
                        {watchItem('valor_vendido') && (
                          <>
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">Valor Vendido:</span>
                              <span className="font-medium text-green-600">
                                R$ {Number(watchItem('valor_vendido') || 0).toFixed(2)}
                              </span>
                            </div>
                            <div className="flex justify-between border-t border-gray-200 dark:border-gray-600 pt-1 mt-1">
                              <span className="text-gray-600 dark:text-gray-400">Margem de Lucro:</span>
                              <span className={`font-bold ${
                                (Number(watchItem('valor_vendido') || 0) - Number(watchItem('preco_custo') || 0)) >= 0 
                                  ? 'text-green-600' 
                                  : 'text-red-600'
                              }`}>
                                {watchItem('preco_custo') && Number(watchItem('preco_custo')) > 0
                                  ? (((Number(watchItem('valor_vendido') || 0) - Number(watchItem('preco_custo') || 0)) / Number(watchItem('preco_custo') || 1)) * 100).toFixed(1)
                                  : '0.0'
                                }%
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  {currentStep > 1 && (
                    <button
                      type="button"
                      onClick={handlePrevStep}
                      className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm md:text-base"
                    >
                      Anterior
                    </button>
                  )}
                  <div className="flex-1" />
                  {currentStep < 3 ? (
                    <button
                      type="button"
                      onClick={handleNextStep}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm md:text-base"
                    >
                      Próximo
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={salvando}
                      className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm md:text-base"
                    >
                      {salvando ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Salvando...
                        </>
                      ) : (
                        <>
                          {editingItem ? 'Atualizar' : 'Adicionar'}
                        </>
                      )}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setShowModalItem(false);
                      setEditingItem(null);
                      setCurrentStep(1);
                      resetItem();
                    }}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm md:text-base"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal Movimento */}
      {showModalMovimento && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto my-4">
            <div className="p-4 md:p-6">
              <div className="flex justify-between items-center mb-4 md:mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
                  Registrar Movimento
                </h2>
                <button
                  onClick={() => {
                    setShowModalMovimento(false);
                    resetMovimento();
                  }}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
              
              <form onSubmit={handleSubmitMovimento(onSubmitMovimento)} className="space-y-4 md:space-y-6">
                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                    Item <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...registerMovimento('item_id', { required: 'Item é obrigatório', valueAsNumber: true })}
                    className="w-full px-3 md:px-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                  >
                    <option value="">Selecione um item</option>
                    {estoque.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.nome} ({item.quantidade} {item.unidade})
                      </option>
                    ))}
                  </select>
                  {errorsMovimento.item_id && (
                    <p className="mt-1 text-xs md:text-sm text-red-600">{errorsMovimento.item_id.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                    Tipo de Movimento <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...registerMovimento('tipo', { required: 'Tipo é obrigatório' })}
                    className="w-full px-3 md:px-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                  >
                    <option value="entrada">Entrada</option>
                    <option value="saida">Saída</option>
                  </select>
                  {errorsMovimento.tipo && (
                    <p className="mt-1 text-xs md:text-sm text-red-600">{errorsMovimento.tipo.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                    Quantidade <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...registerMovimento('quantidade', { 
                      required: 'Quantidade é obrigatória',
                      min: { value: 1, message: 'Quantidade deve ser maior que 0' },
                      valueAsNumber: true
                    })}
                    type="number"
                    className="w-full px-3 md:px-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                    placeholder="0"
                  />
                  {errorsMovimento.quantidade && (
                    <p className="mt-1 text-xs md:text-sm text-red-600">{errorsMovimento.quantidade.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                    Valor Unitário (R$)
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 md:h-5 md:w-5" />
                    <input
                      {...registerMovimento('valor_unitario', { valueAsNumber: true })}
                      type="number"
                      step="0.01"
                      className="w-full pl-10 pr-3 md:pr-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                    Data de Vencimento do Lote
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 md:h-5 md:w-5" />
                    <input
                      {...registerMovimento('data_vencimento_lote')}
                      type="date"
                      className="w-full pl-10 pr-3 md:pr-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                    Motivo <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    {...registerMovimento('motivo', { required: 'Motivo é obrigatório' })}
                    rows={3}
                    className="w-full px-3 md:px-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all resize-none"
                    placeholder="Motivo do movimento"
                  />
                  {errorsMovimento.motivo && (
                    <p className="mt-1 text-xs md:text-sm text-red-600">{errorsMovimento.motivo.message}</p>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all text-sm md:text-base"
                  >
                    Registrar
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModalMovimento(false);
                      resetMovimento();
                    }}
                    className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm md:text-base"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EstoquePage;
